import { useRef, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { DictationButton } from './DictationButton';
import { SpeakButton } from './SpeakButton';

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: React.ReactNode;
  className?: string;
  /** Optional back-navigation href shown to the left of the title. */
  backHref?: string;
}

export function PageHeader({ title, description, breadcrumbs, actions, className, backHref }: PageHeaderProps) {
  return (
    <header className={cn('mb-6', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2">
          <ol className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
            {breadcrumbs.map((crumb, i) => (
              <li key={i} className="flex items-center gap-1.5">
                {i > 0 && <span aria-hidden="true">/</span>}
                {crumb.href ? (
                  <a href={crumb.href} className="hover:text-[var(--color-text-primary)] transition-colors">
                    {crumb.label}
                  </a>
                ) : (
                  <span className={i === breadcrumbs.length - 1 ? 'text-[var(--color-text-primary)]' : ''}>
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {backHref && (
            <a
              href={backHref}
              aria-label="Back"
              className="mt-1 p-1.5 rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </a>
          )}
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{title}</h1>
            {description && (
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">{description}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

// ─── Shared AI Chat Interface ─────────────────────────────────────────────────
// Used by both the Conversational Lesson player and StudentAssist mentor

export interface AIChatMessage {
  role: 'ai' | 'student';
  content: string;
  timestamp?: Date;
  typing?: boolean;
}

export interface AIChatProps {
  messages: AIChatMessage[];
  onSend: (message: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  className?: string;
  maxHeight?: string;
}

export function AIChat({
  messages,
  onSend,
  loading = false,
  disabled = false,
  placeholder = 'Type your response...',
  className,
  maxHeight = '400px',
}: AIChatProps) {
  const bottomRef  = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  }

  function submitMessage() {
    const val = textareaRef.current?.value.trim();
    if (!val || loading || disabled) return;
    onSend(val);
    if (textareaRef.current) textareaRef.current.value = '';
  }

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      {/* Message thread */}
      <div
        className="flex flex-col gap-4 overflow-y-auto p-4"
        style={{ maxHeight }}
        role="log"
        aria-live="polite"
        aria-label="Conversation"
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              'flex',
              msg.role === 'student' ? 'justify-end' : 'justify-start',
            )}
          >
            <div className={msg.role === 'ai' ? 'ai-bubble' : 'student-bubble'}>
              {msg.typing ? (
                <div className="flex gap-1.5 items-center py-1" aria-label="AI is typing">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap flex-1 min-w-0">
                    {msg.content}
                  </p>
                  {msg.role === 'ai' && msg.content.trim() && (
                    <SpeakButton text={msg.content} className="flex-shrink-0 -mr-1 -mt-0.5" />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} aria-hidden="true" />
      </div>

      {/* Input area */}
      <div className="border-t border-[var(--color-border)] p-3 flex gap-2 items-end">
        <label htmlFor="ai-chat-input" className="sr-only">Your response</label>
        <textarea
          ref={textareaRef}
          id="ai-chat-input"
          rows={2}
          disabled={disabled || loading}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex-1 resize-none px-3 py-2.5 text-sm rounded-[var(--radius-md)]',
            'bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]',
            'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)]',
            'focus:outline-none focus:border-[var(--color-accent-primary)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        />
        <DictationButton
          size="md"
          disabled={disabled || loading}
          onTranscript={(text) => {
            const ta = textareaRef.current;
            if (!ta) return;
            const sep = ta.value && !/\s$/.test(ta.value) ? ' ' : '';
            ta.value = ta.value + sep + text;
            ta.focus();
          }}
        />
        <button
          type="button"
          onClick={submitMessage}
          disabled={disabled || loading}
          aria-label="Send message"
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-[var(--radius-md)] flex items-center justify-center',
            'bg-[var(--color-accent-primary)] text-white transition-opacity',
            'hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
